import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import ResultSignupCard from "../components/ResultSignupCard";
const GST = {
  AU: {
    country: "Australia",
    rate: 10,
    currency: "AUD",
    symbol: "$",
    path: "/tools/australia-gst-calculator",
    seoTitle: "Australia GST Calculator (10%) | MoneyFeed",
    seoDescription: "Add or remove 10% Australian GST from any amount. Check the $75,000 threshold and BAS reporting.",
    h1: "Australia GST Calculator",
    intro: "Add GST to a price or pull GST out of a GST-inclusive amount. Australia charges GST at 10% on most goods and services sold or consumed in Australia.",
    soleTrader: "/tools/australia-sole-trader-tax-calculator",
    soleTraderLabel: "Australia sole-trader tax calculator",
    other: "/tools/new-zealand-gst-calculator",
    otherLabel: "New Zealand GST calculator",
    sections: [
      { title: "GST at a glance", text: "GST is a broad-based tax of 10% on most goods, services and other items sold or consumed in Australia. A business registered for GST charges GST on its taxable sales and can claim GST credits for GST included in the price of things it uses in its business." },
      { title: "Do you need to register for GST?", text: "You must register for GST if your GST turnover is $75,000 or more. If your turnover is below $75,000 you can choose to register voluntarily. Non-profit organisations have a $150,000 threshold." },
      { title: "Reporting GST on your BAS", text: "Registered businesses report GST on activity statements. Reporting is monthly if GST turnover is $20 million or more, quarterly if it is less than $20 million, and annually if you are eligible and choose that cycle. You pay the difference between GST you collected and GST credits you claimed." }
    ],
    faqs: [
      { q: "What is the GST rate in Australia?", a: "10%. A GST-inclusive price of $110 therefore contains $10 of GST and a net price of $100." },
      { q: "How do I remove GST from a price?", a: "Divide the GST-inclusive price by 1.10. For example, $110 divided by 1.10 is $100, so the GST in the price is $10." },
      { q: "When must I register for GST?", a: "When your GST turnover reaches $75,000 or more. Below that level, registration is voluntary." },
      { q: "How often do I report GST?", a: "Monthly, quarterly or annually, depending on your GST turnover and eligibility." }
    ],
  },
  NZ: {
    country: "New Zealand",
    rate: 15,
    currency: "NZD",
    symbol: "$",
    path: "/tools/new-zealand-gst-calculator",
    seoTitle: "New Zealand GST Calculator (15%) | MoneyFeed",
    seoDescription: "Add or remove 15% New Zealand GST from any amount. Check the $60,000 threshold and GST returns.",
    h1: "New Zealand GST Calculator",
    intro: "Add GST to a price or pull GST out of a GST-inclusive amount. New Zealand charges GST at 15% on most goods and services in New Zealand.",
    soleTrader: "/tools/new-zealand-sole-trader-tax-calculator",
    soleTraderLabel: "New Zealand sole-trader tax calculator",
    other: "/tools/australia-gst-calculator",
    otherLabel: "Australia GST calculator",
    sections: [
      { title: "GST at a glance", text: "GST is charged at 15% on most goods and services in New Zealand. A GST-registered business adds GST to the price of its supplies, collects that GST and pays it to Inland Revenue." },
      { title: "Do you need to register for GST?", text: "You must register for GST if your turnover was $60,000 or more in the last 12 months, or if you expect it to be $60,000 or more in the next 12 months. Below that threshold you can register voluntarily." },
      { title: "GST returns", text: "GST returns are filed monthly, 2-monthly or 6-monthly. Monthly filing is open to everyone and is required if turnover is more than $24 million. Two-monthly filing applies when turnover is less than $24 million, and 6-monthly filing when turnover is less than $500,000. Returns are filed through myIR." }
    ],
    faqs: [
      { q: "What is the GST rate in New Zealand?", a: "15%. A GST-inclusive price of $115 therefore contains $15 of GST and a net price of $100." },
      { q: "How do I remove GST from a price?", a: "Divide the GST-inclusive price by 1.15. For example, $115 divided by 1.15 is $100, so the GST in the price is $15." },
      { q: "When must I register for GST?", a: "You must register if your turnover was $60,000 or more in the last 12 months, or you expect it to be $60,000 or more in the next 12 months. Below that threshold you can register voluntarily." },
      { q: "How often do I file a GST return?", a: "GST returns can be monthly, 2-monthly or 6-monthly, depending on your turnover. Returns are filed through myIR." }
    ]
  }
};
export default function GstCalculator({ country }) {
  const c = GST[country] || GST.AU;
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("remove");
  const [result, setResult] = useState(null);
  const calculate = () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) return;
    const rate = c.rate / 100;
    if (mode === "add") {
      const gst = value * rate;
      setResult({ net: value, gst: gst, total: value + gst });
    } else {
      const net = value / (1 + rate);
      setResult({ net: net, gst: value - net, total: value });
    }
  };
  const reset = () => {
    setAmount("");
    setResult(null);
  };
  const money = (n) => c.symbol + n.toFixed(2);
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Seo title={c.seoTitle} description={c.seoDescription} />
      <p className="text-sm font-semibold text-emerald-700">{c.country} GST</p>
      <h1 className="mt-2 text-3xl font-bold">{c.h1}</h1>
      <p className="mt-4 text-gray-700">{c.intro}</p>
      <div className="mt-8 rounded-xl border p-6">
        <label className="block text-sm font-medium">Amount ({c.currency})</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter an amount" className="mt-2 w-full rounded-lg border px-3 py-2" />
        <div className="mt-4 flex gap-2">
          <button onClick={() => setMode("add")} className={mode === "add" ? "rounded-lg bg-emerald-600 px-4 py-2 text-white" : "rounded-lg border px-4 py-2"}>Add GST</button>
          <button onClick={() => setMode("remove")} className={mode === "remove" ? "rounded-lg bg-emerald-600 px-4 py-2 text-white" : "rounded-lg border px-4 py-2"}>Remove GST</button>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={calculate} className="rounded-lg bg-emerald-600 px-4 py-2 text-white">Calculate</button>
          <button onClick={reset} className="rounded-lg border px-4 py-2">Reset</button>
        </div>
        {result && (
          <div className="mt-6 rounded-lg bg-emerald-50 p-4">
            <p className="text-sm text-gray-600">GST amount</p>
            <p className="text-2xl font-bold">{money(result.gst)}</p>
            <p className="mt-2">Net amount: {money(result.net)}</p>
            <p>Total including GST: {money(result.total)}</p>
            <p className="mt-2 text-sm text-gray-600">Rate: {c.rate}% GST. This calculator gives an estimate only and is not tax advice.</p>
          </div>
        )}
        {result && <ResultSignupCard resultTotal={money(result.total)} />}
      </div>
      <h2 className="mt-10 text-2xl font-bold">How it works</h2>
      <ol className="mt-4 list-decimal space-y-2 pl-6 text-gray-700">
        <li>Enter an amount.</li>
        <li>Choose Add GST or Remove GST.</li>
        <li>Click Calculate to see the GST amount, the net amount and the total including GST.</li>
      </ol>
      {c.sections.map((s) => (
        <div key={s.title} className="mt-8">
          <h2 className="text-2xl font-bold">{s.title}</h2>
          <p className="mt-2 text-gray-700">{s.text}</p>
        </div>
      ))}
      <div className="mt-10 rounded-xl border p-6">
        <h2 className="text-2xl font-bold">Scan your receipts with MoneyFeed</h2>
        <p className="mt-2 text-gray-700">Keep your invoices and receipts organised so your GST records are easy to check at return time.</p>
        <Link to="/scan-center">Open Scan Center</Link>
      </div>
      <h2 className="mt-10 text-2xl font-bold">Frequently asked questions</h2>
      {c.faqs.map((f) => (
        <div key={f.q} className="mt-6">
          <h3 className="text-lg font-semibold">{f.q}</h3>
          <p className="mt-1 text-gray-700">{f.a}</p>
        </div>
      ))}
      <h2 className="mt-10 text-2xl font-bold">Related calculators</h2>
      <ul className="mt-4 space-y-2">
        <li><Link to={c.soleTrader}>{c.soleTraderLabel}</Link></li>
        <li><Link to={c.other}>{c.otherLabel}</Link></li>
        <li><Link to="/tools/uae-vat-calculator">UAE VAT calculator</Link></li>
        <li><Link to="/tools/saudi-arabia-vat-calculator">Saudi Arabia VAT calculator</Link></li>
        <li><Link to="/tools">All tools</Link></li>
      </ul>
      <p className="mt-8 text-sm text-gray-600">Sources: Australian Taxation Office and Inland Revenue New Zealand. Check the official guidance for your situation before filing.</p>
    </div>
  );
}
